/**
 * Ownership Verification Agent — API client for property-ownership
 * verification v1 (docs/plans/ownership-verification/README.md § API).
 *
 * Seller:
 *   GET  /api/houses/{house_id}/ownership-verification         → VerificationStatus
 *   POST /api/houses/{house_id}/ownership-verification         (multipart) → VerificationStatus
 * Admin:
 *   GET  /api/admin/ownership-verifications?status=<status|all>
 *   GET  /api/admin/ownership-verifications/{id}
 *   GET  /api/admin/ownership-verifications/{id}/documents/{doc_id}   (streamed file)
 *   POST /api/admin/ownership-verifications/{id}/decision  { action, note }
 *
 * JSON calls go through `APIAgent.request` (shared 401 handling; errors carry
 * `.status` / `.statusCode` / `.detail`). The multipart upload cannot — that
 * helper JSON-encodes bodies and caps requests at 10s, while the server runs
 * document extraction synchronously — so it uses raw `fetch` + `FormData` +
 * `credentials: 'include'` (like depositVerificationAgent.js) and throws the
 * same error shape via {@link toApiError}.
 */
import APIAgent, { API_BASE_URL } from './apiAgent';
import { OWNERSHIP_ATTESTATION_VERSION } from '../utils/ownershipVerification';

const ADMIN_BASE = '/api/admin/ownership-verifications';

/** Server runs extraction synchronously on submit — give it room. */
export const SUBMIT_TIMEOUT_MS = 120000;
/** A reject can issue Stripe refunds before responding. */
const DECISION_TIMEOUT_MS = 45000;

const documentUrl = (verificationId, documentId) =>
  `${API_BASE_URL}${ADMIN_BASE}/${encodeURIComponent(verificationId)}/documents/${encodeURIComponent(documentId)}`;

const sellerPath = (houseId) =>
  `/api/houses/${encodeURIComponent(houseId)}/ownership-verification`;

/**
 * Build an Error that looks like the ones `APIAgent.request` throws:
 * `.message` is human-readable, `.status`/`.statusCode` carry the HTTP status,
 * `.detail` is the backend `detail` exactly as sent (e.g. `{code, message}`).
 *
 * @param {Response} response
 * @returns {Promise<Error & {status: number, statusCode: number, detail: any}>}
 */
export async function toApiError(response) {
  let message = `${response.status} ${response.statusText || ''}`.trim();
  let rawDetail;
  try {
    const body = await response.json();
    if (body && body.detail !== undefined) {
      rawDetail = body.detail;
      if (typeof body.detail === 'string') {
        message = body.detail;
      } else if (Array.isArray(body.detail)) {
        message = body.detail.map((e) => e?.msg || JSON.stringify(e)).join('; ');
      } else if (body.detail && typeof body.detail.message === 'string') {
        message = body.detail.message;
      } else {
        message = JSON.stringify(body.detail);
      }
    }
  } catch (_) {
    /* no JSON body */
  }
  const err = new Error(message);
  err.status = response.status;
  err.statusCode = response.status;
  err.detail = rawDetail;
  return err;
}

export const ownershipVerificationAgent = {
  /**
   * Current verification status for a listing (status `not_started` when none).
   * @param {number|string} houseId
   * @returns {Promise<object>} VerificationStatus
   */
  getStatus(houseId) {
    return APIAgent.request(sellerPath(houseId), 'GET');
  },

  /**
   * Submit ownership documents. Resolves with the updated VerificationStatus.
   *
   * @param {number|string} houseId
   * @param {object} args
   * @param {string} args.capacity - sole_owner | co_owner | entity | trust | estate | power_of_attorney | guardian
   * @param {string} [args.capacityDetail]
   * @param {string[]} [args.otherOwners]
   * @param {string} args.proofDocumentType - deed | tax_bill | title_policy
   * @param {File} args.proofFile
   * @param {Array<{file: File, documentType: string}>} [args.authorityDocuments]
   * @param {boolean} args.attestationAccepted - must be true
   * @param {boolean} [args.poaConfirmation] - power_of_attorney only: sent as
   *   `poa_confirmation="true"` (server 422s `poa_confirmation_required` without it)
   * @param {string} [args.attestationVersion]
   * @returns {Promise<object>}
   */
  async submit(
    houseId,
    {
      capacity,
      capacityDetail,
      otherOwners = [],
      proofDocumentType,
      proofFile,
      authorityDocuments = [],
      attestationAccepted,
      attestationVersion = OWNERSHIP_ATTESTATION_VERSION,
      poaConfirmation,
    },
  ) {
    const form = new FormData();
    form.append('capacity', capacity);
    if (capacityDetail && capacityDetail.trim()) {
      form.append('capacity_detail', capacityDetail.trim());
    }
    const owners = (otherOwners || []).map((o) => (o || '').trim()).filter(Boolean);
    if (owners.length > 0) form.append('other_owners', JSON.stringify(owners));
    form.append('proof_document_type', proofDocumentType);
    form.append('proof_file', proofFile);
    const authority = (authorityDocuments || []).filter((d) => d && d.file);
    if (authority.length > 0) {
      form.append(
        'authority_document_types',
        JSON.stringify(authority.map((d) => d.documentType)),
      );
      authority.forEach((d) => form.append('authority_files', d.file));
    }
    form.append('attestation_accepted', attestationAccepted ? 'true' : 'false');
    form.append('attestation_version', attestationVersion);
    if (poaConfirmation) form.append('poa_confirmation', 'true');

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS) : null;
    try {
      const response = await fetch(`${API_BASE_URL}${sellerPath(houseId)}`, {
        method: 'POST',
        credentials: 'include',
        body: form,
        signal: controller ? controller.signal : undefined,
      });
      if (!response.ok) throw await toApiError(response);
      return await response.json();
    } catch (err) {
      if (err && err.name === 'AbortError') {
        const timeoutErr = new Error(
          'The upload took too long. Check your connection and try again.',
        );
        timeoutErr.isTimeout = true;
        throw timeoutErr;
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  },

  // ── Admin ──────────────────────────────────────────────────────────────

  /**
   * Review queue, newest first.
   * @param {object} [opts]
   * @param {string} [opts.status='needs_review'] - any status, or 'all'
   * @returns {Promise<object[]>}
   */
  adminList({ status = 'needs_review' } = {}) {
    const params = new URLSearchParams({ status });
    return APIAgent.request(`${ADMIN_BASE}?${params}`, 'GET');
  },

  /** Full verification detail for the review panel. */
  adminGet(verificationId) {
    return APIAgent.request(`${ADMIN_BASE}/${encodeURIComponent(verificationId)}`, 'GET');
  },

  /**
   * Record a decision.
   * @param {number|string} verificationId
   * @param {object} args
   * @param {'approve'|'reject'|'request_info'|'revoke'} args.action
   * @param {string} [args.note] - required for everything but approve
   * @returns {Promise<object>} updated detail (reject adds `refund: {status, detail}`)
   */
  adminDecide(verificationId, { action, note }) {
    return APIAgent.request(
      `${ADMIN_BASE}/${encodeURIComponent(verificationId)}/decision`,
      'POST',
      { action, note: note ? note.trim() : note },
      {},
      { timeout: DECISION_TIMEOUT_MS },
    );
  },

  /**
   * The admin on/off switch for the ownership publish gate.
   *
   * Contract — GET /api/admin/ownership-verifications/settings
   *   → { enabled, stored_enabled, env_forced_off, updated_at, updated_by:
   *       {id, name, email}|null, history: [{changed_at, changed_by_name,
   *       old_enabled, new_enabled, note}] }   (`enabled` = effective value)
   *   403 `admin_required`.
   */
  adminGetSettings() {
    return APIAgent.request(`${ADMIN_BASE}/settings`, 'GET');
  },

  /**
   * Turn the publish gate on or off.
   *
   * Contract — PUT /api/admin/ownership-verifications/settings
   *   body { enabled: bool, note: string|null } → same shape as GET.
   *   422 `detail.code = "note_required"` when turning OFF without a note.
   *
   * @param {object} args
   * @param {boolean} args.enabled
   * @param {string|null} [args.note] - required when turning off
   */
  adminUpdateSettings({ enabled, note = null }) {
    const trimmed = typeof note === 'string' ? note.trim() : '';
    return APIAgent.request(`${ADMIN_BASE}/settings`, 'PUT', {
      enabled: Boolean(enabled),
      note: trimmed || null,
    });
  },

  /**
   * Replace the list of addresses emailed when a case needs review.
   *
   * Contract — PUT /api/admin/ownership-verifications/settings/notifications
   *   body { emails: [str] } → the full settings shape (see adminGetSettings,
   *   now with `notifications: {emails, stored_emails, default_emails,
   *   allowed_domain, delivery, updated_at, updated_by}`).
   *   422 `detail.code = "invalid_recipient"` with `detail.invalid: [..]`.
   *   An empty list turns notifications off.
   *
   * @param {string[]} emails
   */
  adminUpdateNotificationEmails(emails) {
    return APIAgent.request(`${ADMIN_BASE}/settings/notifications`, 'PUT', {
      emails: Array.isArray(emails) ? emails : [],
    });
  },

  /**
   * Send a test notification to the saved recipients.
   *
   * Contract — POST /api/admin/ownership-verifications/settings/notifications/test
   *   → { sent_to: [..], delivery }; 409 `no_recipients`.
   */
  adminSendTestNotification() {
    return APIAgent.request(
      `${ADMIN_BASE}/settings/notifications/test`,
      'POST',
      null,
      {},
      { timeout: 30000 },
    );
  },

  /**
   * Fetch an uploaded document as a Blob for the admin viewer.
   *
   * The viewer must NOT point an <iframe>/<a> at the stream URL directly: a
   * browser navigation sends `Accept: text/html`, and the CRA dev proxy answers
   * those with index.html (the app renders inside the iframe). A credentialed
   * fetch works identically in dev and prod; the caller renders the Blob via
   * `URL.createObjectURL`. Each fetch is audit-logged server-side.
   *
   * @param {number|string} verificationId
   * @param {number|string} documentId
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @returns {Promise<Blob>} rejects with `.status` (410 = purged) / `.detail`
   */
  async adminFetchDocument(verificationId, documentId, { signal } = {}) {
    const response = await fetch(documentUrl(verificationId, documentId), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/pdf,image/jpeg,image/png' },
      signal,
    });
    if (!response.ok) throw await toApiError(response);
    return response.blob();
  },

  /**
   * URL of the admin document stream (cookie auth, Content-Disposition inline).
   * Fetched by {@link adminFetchDocument}; not used as a navigation target.
   */
  adminDocumentUrl(verificationId, documentId) {
    return documentUrl(verificationId, documentId);
  },
};

export default ownershipVerificationAgent;
